import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ViewMode = 'grid' | 'list' | 'compact';
export type SortBy = 'name' | 'date' | 'size' | 'type';
export type SortDirection = 'asc' | 'desc';

export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

export interface UIState {
  isSidebarOpen: boolean;
  viewMode: ViewMode;
  sortBy: SortBy;
  sortDirection: SortDirection;
  isUploading: boolean;
  isCreatingFolder: boolean;
  isRenaming: boolean;
  selectedItems: {
    files: string[];
    folders: string[];
  };
  notifications: Notification[];
  isMobileView: boolean;
  drawerWidth: number;
  isPreviewOpen: boolean;
  previewFileId: string | null;
  confirmDialog: {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: (() => void) | null;
    severity: 'info' | 'success' | 'warning' | 'error';
  };
  isSelectionMode: boolean;
  /** Upload dialog minimized to sidebar chip; dialog stays logically open in parent. */
  uploadDialogMinimized: boolean;
  /** Single app-level upload dialog open state (MainLayout owns the mount). */
  uploadDialogOpen: boolean;
  /** Optional folder id override when opening the upload dialog. */
  uploadDialogFolderId: string | null;
}

const initialState: UIState = {
  isSidebarOpen: true,
  viewMode: 'grid',
  sortBy: 'name',
  sortDirection: 'asc',
  isUploading: false,
  isCreatingFolder: false,
  isRenaming: false,
  selectedItems: {
    files: [],
    folders: []
  },
  notifications: [],
  isMobileView: window.innerWidth < 768,
  drawerWidth: 240,
  isPreviewOpen: false,
  previewFileId: null,
  confirmDialog: {
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    severity: 'info',
  },
  isSelectionMode: false,
  uploadDialogMinimized: false,
  uploadDialogOpen: false,
  uploadDialogFolderId: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    setSortBy: (state, action: PayloadAction<SortBy>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<SortDirection>) => {
      state.sortDirection = action.payload;
    },
    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setIsCreatingFolder: (state, action: PayloadAction<boolean>) => {
      state.isCreatingFolder = action.payload;
    },
    setIsRenaming: (state, action: PayloadAction<boolean>) => {
      state.isRenaming = action.payload;
    },
    selectItem: (state, action: PayloadAction<{ id: string; type: 'file' | 'folder' }>) => {
      const { id, type } = action.payload;
      const exists = state.selectedItems[`${type}s`].some((item) => item === id);
      
      if (!exists) {
        state.selectedItems[`${type}s`].push(id);
      }
    },
    deselectItem: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedItems.files = state.selectedItems.files.filter((item) => item !== id);
      state.selectedItems.folders = state.selectedItems.folders.filter((item) => item !== id);
    },
    clearSelection: (state) => {
      state.selectedItems = { files: [], folders: [] };
      state.isSelectionMode = false;
    },
    selectMultipleItems: (state, action: PayloadAction<{ id: string; type: 'file' | 'folder' }[]>) => {
      state.selectedItems.files = action.payload.map(item => item.id).filter(id => !state.selectedItems.files.includes(id));
      state.selectedItems.folders = action.payload.map(item => item.id).filter(id => !state.selectedItems.folders.includes(id));
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const id = `${timestamp}-${random}`;
      state.notifications.push({
        ...action.payload,
        id,
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
    },
    setIsMobileView: (state, action: PayloadAction<boolean>) => {
      state.isMobileView = action.payload;
      
      // Auto-close sidebar on mobile
      if (action.payload) {
        state.isSidebarOpen = false;
      } else {
        state.isSidebarOpen = true;
      }
    },
    setDrawerWidth: (state, action: PayloadAction<number>) => {
      state.drawerWidth = action.payload;
    },
    openPreview: (state, action: PayloadAction<string>) => {
      state.isPreviewOpen = true;
      state.previewFileId = action.payload;
    },
    closePreview: (state) => {
      state.isPreviewOpen = false;
      state.previewFileId = null;
    },
    openConfirmDialog: (state, action: PayloadAction<Omit<UIState['confirmDialog'], 'isOpen'>>) => {
      state.confirmDialog = {
        ...action.payload,
        isOpen: true,
      };
    },
    closeConfirmDialog: (state) => {
      state.confirmDialog.isOpen = false;
    },
    toggleSelectionMode: (state) => {
      state.isSelectionMode = !state.isSelectionMode;
      if (!state.isSelectionMode) {
        state.selectedItems = { files: [], folders: [] };
      }
    },
    toggleSelectItem: (state, action: PayloadAction<{ itemId: string, itemType: 'file' | 'folder' }>) => {
      const { itemId, itemType } = action.payload;
      const items = state.selectedItems[`${itemType}s`];
      
      if (items.includes(itemId)) {
        state.selectedItems[`${itemType}s`] = items.filter(id => id !== itemId);
      } else {
        state.selectedItems[`${itemType}s`] = [...items, itemId];
      }
      
      // If nothing is selected, exit selection mode
      if (state.selectedItems.files.length === 0 && state.selectedItems.folders.length === 0) {
        state.isSelectionMode = false;
      }
    },
    selectAllItems: (state, action: PayloadAction<{ fileIds: string[], folderIds: string[] }>) => {
      const { fileIds, folderIds } = action.payload;
      state.selectedItems = {
        files: fileIds,
        folders: folderIds
      };
      state.isSelectionMode = fileIds.length > 0 || folderIds.length > 0;
    },
    setUploadDialogMinimized: (state, action: PayloadAction<boolean>) => {
      state.uploadDialogMinimized = action.payload;
    },
    openUploadDialog: (state, action: PayloadAction<{ folderId?: string | null } | undefined>) => {
      state.uploadDialogOpen = true;
      state.uploadDialogMinimized = false;
      if (action.payload && 'folderId' in action.payload) {
        state.uploadDialogFolderId = action.payload.folderId ?? null;
      }
    },
    closeUploadDialog: (state) => {
      state.uploadDialogOpen = false;
      state.uploadDialogMinimized = false;
      state.uploadDialogFolderId = null;
    },
  },
});

export const {
  toggleSidebar,
  setViewMode,
  setSortBy,
  setSortDirection,
  setIsUploading,
  setIsCreatingFolder,
  setIsRenaming,
  selectItem,
  deselectItem,
  clearSelection,
  selectMultipleItems,
  addNotification,
  removeNotification,
  setIsMobileView,
  setDrawerWidth,
  openPreview,
  closePreview,
  openConfirmDialog,
  closeConfirmDialog,
  toggleSelectionMode,
  toggleSelectItem,
  selectAllItems,
  setUploadDialogMinimized,
  openUploadDialog,
  closeUploadDialog,
} = uiSlice.actions;

export default uiSlice.reducer;